import { BaseScreen } from '@goodboydigital/astro';
import { Graphics, Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import { PerkAdButton } from '../ui/buttons/PerkAdButton';
import { PerkButton } from '../ui/buttons/PerkButton';
import { ShopButton } from '../ui/buttons/ShopButton';
import LocalisedText from '../ui/LocalisedText';
import { StrUtils } from '../utils/StrUtils';
import { CoinCounterWidget } from './../ui/CoinCounterWidget';
import { Panel } from './../ui/Panel';
import { PositionDisplayWidget } from './../ui/PositionDisplayWidget';
import { TimerDisplayWidget } from './../ui/TimerDisplayWidget';

export class ResultsScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public nextText!: LocalisedText;
    public positionTitle!: LocalisedText;
    public homeButton!: NineSliceButton;
    public shopButton?: ShopButton;
    public replayButton!: NineSliceButton;
    public rewardButton!: PerkAdButton;
    public nextButton!: NineSliceButton;
    public resultsPanel!: Panel;

    public bonusCoins!: CoinCounterWidget;
    public collectedCoins!: CoinCounterWidget;
    public totalCoins!: CoinCounterWidget;
    public timerDisplay!: TimerDisplayWidget;
    public positionDisplay!: PositionDisplayWidget;

    constructor(app: App)
    {
        super(app);
        this.app = app;
    }

    async init(): Promise<void>
    {
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.resultsPanel = new Panel({
            w: 720,
            h: 540,
        });
        this.resultsPanel.view.sortableChildren = true;
        this.view.addChild(this.resultsPanel.view);

        const homeIcon = Sprite.from(Shared.assets.getTexture('icon-home'));

        this.homeButton = new NineSliceButton({
            icon: homeIcon,
            bgAsset: 'button-ninesliced-magenta',
            w: homeIcon.width * 1.5,
            h: homeIcon.height * 1.5,
        });
        this.view.addChild(this.homeButton.view);
        this.homeButton.onPress.connect(() =>
        {
            this.app.controller.toHome();
            Shared.game.idle();
        });
        
        if(!Settings.browserLite)
        {
            this.shopButton = new ShopButton();
            this.view.addChild(this.shopButton.view);
            this.shopButton.onPress.connect(() =>
            {
                this.app.controller.toGarage();
            });
        }
        
        this.title = new LocalisedText('resultsScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            align: 'center',
            lineJoin: 'round',
            fontSize: 44,
            maxWidth: 650,
        });
        this.title.anchor.set(0.5);
        this.resultsPanel.view.addChild(this.title);
        this.title.y -= 210;

        this.positionTitle = new LocalisedText('resultsScreen_position', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 32,
            maxWidth: 220,
        });
        this.positionTitle.anchor.set(0.5);
        this.positionTitle.x -= 170;
        this.positionTitle.y -= 140;
        this.resultsPanel.view.addChild(this.positionTitle);

        const replayIcon = Sprite.from(Shared.assets.getTexture('icon-replay'));

        replayIcon.anchor.set(0.5);
        replayIcon.scale.set(0.7);

        this.replayButton = new NineSliceButton({
            icon: replayIcon,
            accessibleTitle: 'GO',
            bgAsset: 'button-ninesliced-yellow',
            w: 100,
            h: 100,
        });
        this.resultsPanel.view.addChild(this.replayButton.view);
        this.replayButton.view.x = -270;
        this.replayButton.view.y = 180;
        this.replayButton.onPress.connect(() => {
            this.app.analytics.trackEvent(AnalyticsEvent.GAME_REPLAY);
            this.app.controller.playPrevRace();
        });

        this.rewardButton = new PerkAdButton({
            text: 'resultsScreen_rewardButton',
            width: 440,
            height: 145,
            fontSize: 40,
        });
        this.resultsPanel.view.addChild(this.rewardButton.view);
        this.rewardButton.view.y = 180;
        this.rewardButton.view.x = 125;
        this.rewardButton.onPress.connect(async () =>
        {
            const reward = this.app.reward.getRandomReward(this.app.reward.getPostRacePerks());
            this.app.analytics.trackEvent(AnalyticsEvent.RACE_RACEREWARD_CLICK, { game_item: `racereward|${reward.id}` });
            this.rewardButton.hide(false);
            this.updatePanelBottomButtons();
            const currentCoins = Shared.user.getCoins();

            await this.app.reward.claimPostRacePerk(reward, this.rewardButton.adIcon.visible);

            const earnedCoins = Math.max(Shared.user.getCoins() - currentCoins, 0);

            this.bonusCoins.coins += earnedCoins;
            this.totalCoins.coins += earnedCoins;
        });

        const next = new Sprite();
        const nextIcon = Sprite.from(Shared.assets.getTexture('icon-play'));

        next.addChild(nextIcon);
        nextIcon.anchor.set(0.5);
        nextIcon.scale.set(0.7);

        this.nextButton = new NineSliceButton({
            icon: next,
            accessibleTitle: 'GO',
            bgAsset: 'button-ninesliced-yellow',
            w: 100,
            h: 100,
        });
        this.resultsPanel.view.addChild(this.nextButton.view);
        this.nextButton.view.x = -145;
        this.nextButton.view.y = 180;
        this.nextButton.onPress.connect(() =>
        {
            this.app.controller.playNextRace();
        });

        const divider = new Graphics();

        divider.lineStyle(2, 0xffffff)
            .moveTo(-280, 20)
            .lineTo(280, 20);
        this.resultsPanel.view.addChild(divider);

        this.collectedCoins = new CoinCounterWidget('resultsScreen_collectedCoins');
        this.collectedCoins.open();
        this.collectedCoins.view.position.set(45, -120);
        this.resultsPanel.view.addChild(this.collectedCoins.view);

        this.bonusCoins = new CoinCounterWidget('resultsScreen_bonusCoins');
        this.bonusCoins.open();
        this.bonusCoins.view.position.set(45, -35);
        this.resultsPanel.view.addChild(this.bonusCoins.view);

        this.totalCoins = new CoinCounterWidget('resultsScreen_totalCoins');
        this.totalCoins.open();
        this.totalCoins.view.position.set(45, 70);
        this.resultsPanel.view.addChild(this.totalCoins.view);

        this.timerDisplay = new TimerDisplayWidget(Shared.game);
        this.timerDisplay.open();
        this.timerDisplay.view.position.set(-230, 70);
        this.resultsPanel.view.addChild(this.timerDisplay.view);

        this.positionDisplay = new PositionDisplayWidget(Shared.game);
        this.positionDisplay.open();
        this.positionDisplay.view.position.set(this.positionTitle.x, -60);
        this.resultsPanel.view.addChild(this.positionDisplay.view);
    }

    show(): void
    {
        this.resize(this.width, this.height);
        
        this.app.analytics.trackState(AnalyticsState.RACE_RESULTS);
        this.app.analytics.trackEvent(AnalyticsEvent.GAME_LEVEL_COMPLETE);
        
        this.view.visible = true;
        
        this.rewardButton.setAdIconVisible(this.app.ads.config.postRaceRewardAds && this.app.ads.canShowAd());

        this.timerDisplay.setDisplay(StrUtils.formatRaceTime(Shared.game.race.time));

        this.collectedCoins.coins = Shared.game.race.collectedCoins;
        this.bonusCoins.coins = Shared.game.race.bonusCoins;
        this.totalCoins.coins = Shared.game.race.coins;

        this.positionDisplay.refresh(Shared.game.race.position, Shared.game.race.players);

        if (this.app.reward.canClaimPostRacePerk()) this.rewardButton.show();
        this.updatePanelBottomButtons();
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.resultsPanel.view.position.copyFrom(this.app.stageCenter as Point);

        this.homeButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );
        
        if(this.shopButton)
        {
            this.shopButton.view.position.set(
                this.app.stageLeft + 220,
                this.app.stageTop + 90,
            );
        }
        
    }

    private updatePanelBottomButtons(): void
    {
        this.rewardButton.view.x = 125;

        if (this.rewardButton.view.visible)
        {
            this.replayButton.view.x = -270;
            this.nextButton.view.x = -145;
        }
        else
        {
            this.replayButton.view.x = -100;
            this.nextButton.view.x = 100;
        }
    }
}
